import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Button,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  saveUnsavedChanges,
  selectAssessmentDetails,
  selectSelectedContacts,
  updateDetails,
} from '../../redux/reducers/assessmentReducer';
import { selectNameList } from '../../redux/reducers/organizationReducer';

function AssessmentDetails() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const assessmentDetails = useSelector(selectAssessmentDetails);
  const contacts = useSelector(selectSelectedContacts);
  const contactNames = useSelector((state) => selectNameList(state, contacts));
  const [textFields, setTextFields] = useState({
    auditors: assessmentDetails.auditors ?? '',
    auditees: assessmentDetails.auditees ?? '',
    scope: assessmentDetails.scope ?? '',
    criteria: assessmentDetails.criteria ?? '',
    objectives: assessmentDetails.objectives ?? '',
  });

  const handleTextChange = (event) => {
    const { name, value } = event.target;
    const newState = { ...textFields };
    newState[name] = value;
    setTextFields(newState);
  };

  const handleChange = () => {
    const newDetails = {
      auditors: textFields.auditors,
      auditees: textFields.auditees,
      scope: textFields.scope,
      criteria: textFields.criteria,
      objectives: textFields.objectives,
    };
    dispatch(updateDetails(newDetails));
  };

  const handleCopyContacts = () => {
    const newAuditees = `${contactNames}${textFields.auditees ? `, ${textFields.auditees}` : ''}`;
    setTextFields({
      ...textFields,
      auditees: newAuditees,
    });
    const newDetails = {
      auditees: newAuditees,
      scope: textFields.scope,
      criteria: textFields.criteria,
      objectives: textFields.objectives,
    };
    dispatch(updateDetails(newDetails));
  };

  useEffect(() => () => {
    dispatch(saveUnsavedChanges(''));
  }, []);

  return (
    <Container disableGutters>
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography id='assessment-auditees-title'>{t('assessment.details.auditeesTitle')}</Typography>
          <Typography variant="body2">{t('assessment.details.auditeesDescription')}</Typography>
          <Stack direction="row" justifyContent="end" sx={{ pr: 2 }}>
            <Button
              id="copy-from-contacts"
              variant="outlined"
              size="small"
              onClick={handleCopyContacts}
              endIcon={<ContentCopyIcon />}
            >
              <Typography variant="buttonSmall">{t('assessment.details.buttonCopyContacts')}</Typography>
            </Button>
          </Stack>
          <TextField
            id="auditees-textfield"
            name="auditees"
            value={textFields.auditees}
            onChange={handleTextChange}
            onBlur={handleChange}
            multiline
            rows="4"
            InputLabelProps={{
              shrink: true,
            }}
            label={t('assessment.details.auditees')}
            sx={{ backgroundColor: 'white' }}
          />
        </Stack>
        <TextField
          id="auditors-textfield"
          name="auditors"
          value={textFields.auditors}
          onChange={handleTextChange}
          onBlur={handleChange}
          multiline
          rows="4"
          InputLabelProps={{
            shrink: true,
          }}
          label={t('assessment.details.auditors')}
          sx={{ backgroundColor: 'white' }}
        />
        <TextField
          id="scope-textfield"
          name="scope"
          defaultValue={textFields.scope}
          onChange={handleTextChange}
          onBlur={handleChange}
          multiline
          minRows="4"
          InputLabelProps={{
            shrink: true,
          }}
          label={t('assessment.details.scope')}
          sx={{ backgroundColor: 'white' }}
        />
        <TextField
          id="criteria-textfield"
          name="criteria"
          defaultValue={textFields.criteria}
          onChange={handleTextChange}
          onBlur={handleChange}
          multiline
          minRows="4"
          InputLabelProps={{
            shrink: true,
          }}
          label={t('assessment.details.criteria')}
          sx={{ backgroundColor: 'white' }}
        />
        <TextField
          id="objectives-textfield"
          name="objectives"
          defaultValue={textFields.objectives}
          onChange={handleTextChange}
          onBlur={handleChange}
          multiline
          minRows="4"
          InputLabelProps={{
            shrink: true,
          }}
          label={t('assessment.details.objectives')}
          sx={{ backgroundColor: 'white' }}
        />
      </Stack>
    </Container>
  );
}

export default AssessmentDetails;
